import { CustomAccordionExpandIcon } from '@aibees/elephant/components/custom-accordion-expand-icon';
import { useFiltersTags } from '@aibees/elephant/feature/search-filters/components/filter-tags/hooks/use-filters-tags';
import { useIsAppliedFiltersEmpty } from '@aibees/elephant/feature/search-filters/filters/applied-filters/hooks/use-is-applied-filters-empty';
import {
  useSearchTableFromRoute,
  useSearchQueryFilter,
} from '@aibees/elephant/hooks';
import { Label, TextEllipsis } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Typography,
} from '@mui/material';
import { memo, useState } from 'react';
import {
  flattenFilterTagItems,
  getTagsCount,
} from '../../components/filter-tags/utils';
import { AppliedFiltersTags } from './components/applied-filters-tags';
import { useClosedFilters } from './hooks/use-closed-filters';
import { useFilterParent } from './hooks/use-filter-parent';
import type { AppliedFiltersProps as Props } from './types';

const ACCORDION_SLOT_PROPS = {
  transition: { unmountOnExit: true },
};

export const AppliedFilters = memo(
  ({ className, openItem, customAppliedFiltersTags, ...filter }: Props) => {
    const table = useSearchTableFromRoute();
    const { params } = useSearchQueryFilter();
    const filterBase = useFilterParent(table, filter.field ?? '');
    const closedFilters = useClosedFilters(filterBase, openItem);
    const closedFiltersTags = useFiltersTags(
      table,
      params,
      closedFilters ?? []
    );
    const isAnyNotEmpty = useIsAppliedFiltersEmpty(closedFiltersTags);
    const [open, setOpen] = useState<boolean>(false);

    if (!isAnyNotEmpty) return null;

    const handleChange = () => {
      setOpen((prev) => !prev);
    };

    return (
      <>
        {closedFiltersTags.map((closedFilterTags) => {
          const flattenedTagItems = flattenFilterTagItems(closedFilterTags);
          const tagsCount = getTagsCount(closedFilterTags);
          if (!tagsCount) return null;
          return (
            <Box
              className={classnames('AppliedFilters', className)}
              key={closedFilterTags.name}
            >
              <Accordion
                key={closedFilterTags.name}
                variant="outlined"
                color="primary"
                expanded={open}
                slotProps={ACCORDION_SLOT_PROPS}
              >
                <AccordionSummary
                  expandIcon={<CustomAccordionExpandIcon open={open} />}
                  onClick={handleChange}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flex={1}
                    paddingLeft={0.5}
                  >
                    <Badge color="primary" variant="dot" size={6} />
                    <TextEllipsis variant="body4" color="primary" w="200px">
                      Applied filters in {closedFilterTags?.name}
                    </TextEllipsis>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Label variant="filled" color="default">
                      <Typography variant="body4">{tagsCount}</Typography>
                    </Label>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {customAppliedFiltersTags ? (
                    customAppliedFiltersTags
                  ) : (
                    <AppliedFiltersTags tags={flattenedTagItems} {...filter} />
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </>
    );
  }
);

AppliedFilters.displayName = 'AppliedFilters';
